"use client";

import Link from "next/link";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sun, Moon, Menu, X } from "lucide-react";

export default function CookiesPage() {
  const [isLightTheme, setIsLightTheme] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light") {
      setIsLightTheme(true);
      document.documentElement.classList.add("light");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isLightTheme;
    setIsLightTheme(newTheme);
    localStorage.setItem("theme", newTheme ? "light" : "dark");
    if (newTheme) {
      document.documentElement.classList.add("light");
    } else {
      document.documentElement.classList.remove("light");
    }
  };

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/contact", label: "Contact" },
    { href: "/terms", label: "Terms" },
    { href: "/privacy", label: "Privacy" },
    { href: "/cookies", label: "Cookies" },
  ];

  return (
    <div className="min-h-screen bg-background flex flex-col landing-theme">
      {/* ============================================ */}
      {/* NAVIGATION - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border/30 glass-nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            {/* Logo - proportional sizing */}
            <Link href="/" className="flex items-center">
              <span className="text-xl portrait:text-[clamp(1.25rem,2.5vw,1.75rem)] font-bold text-foreground tracking-tight">Lateral</span>
            </Link>

            {/* Separator - Desktop only (lg+) */}
            <div className="hidden lg:block separator-line mx-6" />

            {/* Menu Items - Desktop only (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-6 portrait:gap-[clamp(1.5rem,2vw,2rem)] flex-1">
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="text-sm portrait:text-[clamp(0.875rem,1.2vw,1rem)] text-foreground-secondary hover:text-foreground transition-colors"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* Auth Buttons + Theme Toggle - Desktop (lg+), proportional sizing */}
            <div className="hidden lg:flex items-center gap-3 portrait:gap-[clamp(0.75rem,1.2vw,1rem)]">
              {/* Theme Toggle - Desktop */}
              <button
                onClick={toggleTheme}
                className="ml-2 p-2 portrait:p-[clamp(0.5rem,0.8vw,0.75rem)] rounded-lg text-foreground-secondary hover:text-foreground hover:bg-white/5 transition-all"
                aria-label="Toggle theme"
              >
                {isLightTheme ? (
                  <Moon className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                ) : (
                  <Sun className="w-5 h-5 portrait:w-[clamp(1.25rem,2vw,1.75rem)] portrait:h-[clamp(1.25rem,2vw,1.75rem)]" />
                )}
              </button>
            </div>

            {/* Mobile/Tablet Controls */}
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-foreground-secondary hover:text-foreground transition-colors"
                aria-label="Toggle theme"
              >
                {isLightTheme ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 text-foreground hover:text-foreground-secondary transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu Overlay */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="lg:hidden absolute top-full left-0 right-0 bg-background/95 backdrop-blur-md border-b border-border/30 shadow-lg"
            >
              <div className="py-6 flex flex-col items-center gap-4">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    onClick={() => setMobileMenuOpen(false)}
                    className="text-base text-foreground-secondary hover:text-foreground transition-colors py-2"
                  >
                    {link.label}
                  </Link>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      {/* ============================================ */}
      {/* MAIN CONTENT - NARROWER WIDTH */}
      {/* ============================================ */}
      <main className="flex-1 pt-[73px]">
        <div className="max-w-4xl mx-auto px-6 py-12 md:py-16 lg:py-20">
          {/* Page Title */}
          <div className="mb-12">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4">
              Cookie policy
            </h1>
            <p className="text-lg text-foreground-secondary mb-8">
              Last Updated: January 4, 2026
            </p>
            <div className="h-px bg-foreground/10" />
          </div>

          <div className="prose prose-invert max-w-none">
            <p className="text-foreground-secondary text-lg mb-12 leading-relaxed">
              This Cookie Policy explains how <span className="text-foreground font-medium">Lateral LTD</span> (&quot;We&quot;, &quot;Us&quot;, or &quot;Our&quot;) uses cookies and similar technologies to recognize you when you visit our website and use the Lateral Teams Apps platform. It explains what these technologies are and why we use them, as well as your rights to control our use of them.
            </p>

            {/* Section 1 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">1. What Are Cookies?</h2>
              <p className="text-foreground-secondary leading-relaxed">
                Cookies are small data files that are placed on your computer or mobile device when you visit a website. They are widely used by website owners to make their websites work, or to work more efficiently, as well as to provide reporting information.
              </p>
            </section>

            {/* Section 2 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">2. How We Use Cookies</h2>
              <p className="text-foreground-secondary leading-relaxed">
                We use cookies for several reasons. Some cookies are required for technical reasons in order for our Service to operate (we refer to these as &quot;essential&quot; or &quot;strictly necessary&quot; cookies). Other cookies enable us to track and target the interests of our users to enhance the experience on our platform.
              </p>
            </section>

            {/* Section 3 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">3. Types of Cookies We Use</h2>

              <div className="space-y-6">
                {/* Cookie Type A */}
                <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
                  <h3 className="text-xl font-semibold text-foreground mb-3">A. Strictly Necessary Cookies</h3>
                  <p className="text-foreground-secondary mb-3 leading-relaxed">
                    These cookies are strictly necessary to provide you with services available through our website and to use some of its features, such as access to secure areas (logging in).
                  </p>
                  <ul className="space-y-2 text-foreground-secondary">
                    <li>
                      <strong className="text-foreground">Example:</strong> Storing your session token so you remain logged in while navigating between pages.
                    </li>
                    <li>
                      <strong className="text-foreground">Consent:</strong> These cannot be switched off in our systems.
                    </li>
                  </ul>
                </div>

                {/* Cookie Type B */}
                <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
                  <h3 className="text-xl font-semibold text-foreground mb-3">B. Performance and Analytics Cookies</h3>
                  <p className="text-foreground-secondary mb-3 leading-relaxed">
                    These cookies collect information that is used either in aggregate form to help us understand how our website is being used or how effective our marketing campaigns are, or to help us customize our website for you.
                  </p>
                  <ul className="space-y-2 text-foreground-secondary">
                    <li>
                      <strong className="text-foreground">Example:</strong> Google Analytics (to see which pages are most popular).
                    </li>
                  </ul>
                </div>

                {/* Cookie Type C */}
                <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
                  <h3 className="text-xl font-semibold text-foreground mb-3">C. Functionality Cookies</h3>
                  <p className="text-foreground-secondary mb-3 leading-relaxed">
                    These are used to recognize you when you return to our website. This enables us to personalize our content for you and remember your preferences.
                  </p>
                  <ul className="space-y-2 text-foreground-secondary">
                    <li>
                      <strong className="text-foreground">Example:</strong> Remembering your language choice or the specific AI tone settings you selected previously.
                    </li>
                  </ul>
                </div>

                {/* Cookie Type D */}
                <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
                  <h3 className="text-xl font-semibold text-foreground mb-3">D. Targeting/Marketing Cookies</h3>
                  <p className="text-foreground-secondary mb-3 leading-relaxed">
                    These cookies record your visit to our website, the pages you have visited, and the links you have followed. We may use this information to make our website and the advertising displayed on it more relevant to your interests.
                  </p>
                  <ul className="space-y-2 text-foreground-secondary">
                    <li>
                      <strong className="text-foreground">Example:</strong> LinkedIn Insights Tag or Meta Pixel (if used for retargeting).  
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">4. How to Control Cookies</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">
                You have the right to decide whether to accept or reject cookies.
              </p>
              <ul className="space-y-3 text-foreground-secondary list-disc pl-5">
                <li>
                  <strong className="text-foreground">Cookie Consent Manager:</strong> You can change your cookie preferences at any time by clicking the &quot;Cookie Settings&quot; link in the footer of our website.
                </li>
                <li>
                  <strong className="text-foreground">Browser Controls:</strong> You can set or amend your web browser controls to accept or refuse cookies. If you choose to reject cookies, you may still use our website, though your access to some functionality and areas of our website may be restricted.
                </li>
              </ul>
            </section>

            {/* Section 5 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Updates to This Policy</h2>
              <p className="text-foreground-secondary leading-relaxed">
                We may update this Cookie Policy from time to time in order to reflect, for example, changes to the cookies we use or for other operational, legal, or regulatory reasons. Please therefore re-visit this Cookie Policy regularly to stay informed about our use of cookies and related technologies.
              </p>
            </section>

            {/* Section 6 */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Contact Us</h2>
              <p className="text-foreground-secondary mb-4 leading-relaxed">
                If you have any questions about our use of cookies or other technologies, please email us at:
              </p>
              <div className="bg-foreground/5 border border-foreground/10 rounded-xl p-6 hover:border-foreground/20 transition-colors">
                <a
                  href="mailto:info@lateralteams.com"
                  className="text-accent hover:underline text-lg font-medium"
                >
                  info@lateralteams.com
                </a>
              </div>
            </section>
          </div>
        </div>
      </main>

      {/* ============================================ */}
      {/* FOOTER - MATCHING LANDING PAGE */}
      {/* ============================================ */}
      <footer className="relative border-t border-border pt-6 pb-0 bg-background">
        <div className="max-w-7xl mx-auto px-6">
          {/* Back to Top */}
          <div className="flex justify-end mb-6">
            <a
              href="#"
              onClick={(e) => {
                e.preventDefault();
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              className="text-sm text-foreground-secondary hover:text-foreground transition-colors flex items-center gap-1"
            >
              Back to top <span className="text-base">↑</span>
            </a>
          </div>

          {/* Footer content - responsive structure */}
          {/* Mobile & Tablet (< lg): Centered layout */}
          <div className="lg:hidden flex flex-col items-center gap-6 pb-6">
            <Link href="/" className="flex items-center">
              <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
            </Link>
            
            <div className="flex flex-wrap justify-center gap-x-5 gap-y-2 text-sm text-foreground-secondary">
              <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
              <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
              <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
              <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
            </div>
            <div className="text-center space-y-1">
              <p className="text-sm text-foreground-secondary">
                20-22 Wenlock Road, London, UK
              </p>
              <p className="text-sm text-foreground-secondary">
                info@lateralteams.com
              </p>
            </div>
            <p className="text-sm text-foreground-muted text-center">
              © 2026 Lateral Teams · All rights reserved
            </p>
          </div>

          {/* Desktop (lg+): Multi-row layout */}
          <div className="hidden lg:block pb-6">
            {/* Top row: Logo + Main Nav */}
            <div className="flex items-center gap-8 mb-6">
              <Link href="/" className="flex items-center shrink-0">
                <span className="text-xl font-bold text-foreground tracking-tight">Lateral</span>
              </Link>
            </div>
            {/* Bottom row: Legal + Company Info + Copyright */}
            <div className="flex items-center justify-between gap-8">
              <div className="flex items-center gap-5 text-sm text-foreground-secondary">
                <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
                <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
                <Link href="/cookies" className="hover:text-foreground transition-colors">Cookies</Link>
                <Link href="/contact" className="hover:text-foreground transition-colors">Contact</Link>
              </div>
              <div className="flex items-center gap-6 text-sm text-foreground-secondary">
                <span>20-22 Wenlock Road, London, UK</span>
                <span>·</span>
                <span>info@lateralteams.com</span>
              </div>
              <p className="text-sm text-foreground-muted shrink-0">
                © 2026 Lateral Teams · All rights reserved
              </p>
            </div>
          </div>
        </div>

        {/* Large Lateral text - 60% visible, 40% hidden - consistent on all screens */}
        <div className="relative h-[14vw] mt-4 overflow-hidden">
          <span
            className="absolute left-1/2 -translate-x-1/2 bottom-[-55%] text-[23vw] font-bold leading-none tracking-[0.02em] select-none pointer-events-none whitespace-nowrap"
            style={{ color: "var(--watermark-color)" }}
          >
            Lateral
          </span>
        </div>
      </footer>
    </div>
  );
}
